import React from "react";
import useBrandAsset from "@/lib/useBrandAsset";
import TOKENS from "@/lib/designTokens";

/**
 * XPlacement — the dedicated public placement region for the X brand
 * ambassador character. Used in Hero, Affiliate, Supplies, About, and Social
 * preview surfaces. When the real X asset is uploaded to the Brand Asset
 * Library with tag `mascot.brand-ambassador`, every <XPlacement /> in the app
 * instantly picks it up — no redesign required.
 *
 * Variants:
 *   hero      — large drop-shadowed centerpiece (default)
 *   hero-tall — square portrait, max-h 520
 *   inline    — 80×80 inline mascot
 *   header    — 32×32 header lockup
 *   og        — 300×300 social preview (used by Phase 11 OG generator)
 *
 * Zones:
 *   home-hero · affiliate · supplies · about · social
 *   (purely descriptive — used to build data-testids + future analytics)
 */

const VARIANT_CLASS = {
  hero: "max-h-[420px] drop-shadow-[0_0_60px_rgba(182,255,26,0.25)]",
  "hero-tall": "max-h-[520px] aspect-[1/1] object-contain drop-shadow-[0_0_70px_rgba(182,255,26,0.3)]",
  inline: "h-20 w-20",
  header: "h-8 w-8",
  og: "h-72 w-72",
};

export default function XPlacement({
  zone = "home-hero",
  variant = "hero",
  alt = "Lifted",
  className = "",
}) {
  const x = useBrandAsset(TOKENS.tags.xMascot, { fallback: TOKENS.tags.rocket });
  const cls = `${VARIANT_CLASS[variant] || VARIANT_CLASS.hero} ${className}`.trim();

  if (!x.url) {
    // Reserved empty placement region — keeps layout stable until X is uploaded.
    return (
      <div data-testid={`x-placement-${zone}-empty`}
           aria-hidden="true"
           className={`${cls} grid place-items-center text-lifted-mute border border-dashed border-white/10 ${TOKENS.radius.tile}`}>
        <span className="font-bebas tracking-[0.18em] uppercase text-[0.65rem]">X · placement</span>
      </div>
    );
  }
  return (
    <img
      src={x.url}
      alt={alt}
      data-testid={`x-placement-${zone}`}
      data-x-zone={zone}
      data-x-variant={variant}
      className={cls}
      loading="lazy"
    />
  );
}
